/**
 * createSpaceThemeScope — la portée de thème d'un espace du design system (l'élève, le tuteur).
 *
 * À l'entrée le thème de l'espace (d'après la préférence d'apparence de l'appareil), à la sortie la
 * main rendue au thème d'avant (une autre coquille, la déconnexion).
 *
 * **Le thème appartient à l'espace, pas à un écran.** Plusieurs écrans le tiennent, parfois en même
 * temps : la coquille, et les écrans poussés qui en sortent (le live et sa note chez l'élève). Ils ne
 * sortent pas dans l'ordre où ils sont entrés : une route remontée par la pile (un live rouvert sous
 * une nouvelle clé) entre pendant que l'ancienne carte est encore montée, le temps de sa fermeture.
 * Chacun retenait « son » thème d'avant et le rendait en sortant : le dernier à sortir gagnait, et le
 * live restait en clair jusqu'à sa propre sortie. Les portées se comptent donc, par espace : le thème
 * d'avant l'espace est retenu par la première qui entre, et rendu par la dernière qui sort.
 *
 * Il porte les mêmes DEUX portées que le parent : le thème du design system, et sur iOS l'apparence
 * native (le clavier, les alertes et les sélecteurs du système), posée par la même règle
 * (`parentAppearanceAt`, commune aux espaces). À la sortie de l'espace, l'apparence repart au clair
 * que le reste de l'app attend.
 */
import { useEffect, useLayoutEffect } from "react"
import { Platform } from "react-native"
import { UnistylesRuntime } from "react-native-unistyles"

import { applyAppearanceMove, parentAppearanceAt } from "./parentAppearance"
import { type ParentAppearancePreference } from "./parentAppearancePreference"

type ThemeName = typeof UnistylesRuntime.themeName

/** Ce que l'espace lit à chaque rendu : son thème du moment, et la préférence qui l'a donné. */
export type SpaceTheme = { theme: ThemeName; preference: ParentAppearancePreference }

/** Une portée par espace : son compteur d'écrans montés et le thème d'avant lui sont les siens. */
export function createSpaceThemeScope(useSpaceTheme: () => SpaceTheme): () => void {
    const space: { holders: number; themeBefore: ThemeName | undefined } = { holders: 0, themeBefore: undefined }

    return function useSpaceThemeScope(): void {
        const { theme, preference } = useSpaceTheme()

        // Thème posé AVANT la première peinture (`useLayoutEffect`), et pas après comme chez le parent :
        // un espace qui s'ouvre en Sombre (le choix, ou l'appareil) se verrait une première image claire.
        useLayoutEffect(() => {
            if (space.holders === 0) space.themeBefore = UnistylesRuntime.themeName
            space.holders += 1

            return () => {
                space.holders -= 1
                // Un autre écran de l'espace le tient encore : son thème reste.
                if (space.holders > 0) return
                const before = space.themeBefore
                space.themeBefore = undefined
                if (before) UnistylesRuntime.setTheme(before)
                applyAppearanceMove(parentAppearanceAt("exit", Platform.OS))
            }
        }, [])

        // Retour d'un autre espace : sa portée sort dans le même commit que la nôtre entre, mais en
        // `useEffect`, donc APRÈS notre entrée. Elle rend alors son thème d'avant par-dessus le nôtre.
        // Ce thème-là est le vrai « d'avant » : on le retient, et on reprend le nôtre, dans la même vague
        // d'effets, avant toute peinture.
        useEffect(() => {
            const current = UnistylesRuntime.themeName
            if (current === theme) return
            space.themeBefore = current
            UnistylesRuntime.setTheme(theme)
            // Une seule fois, à l'entrée : ensuite, c'est l'effet du thème qui suit la préférence.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])

        useEffect(() => {
            applyAppearanceMove(parentAppearanceAt("enter", Platform.OS, preference))
        }, [preference])

        useLayoutEffect(() => {
            if (UnistylesRuntime.themeName !== theme) UnistylesRuntime.setTheme(theme)
        }, [theme])
    }
}
